import React, { useState } from "react";
import List from "@material-ui/core/List";
import { Form, Formik } from "formik";
import { Paper, Button, CircularProgress } from "@material-ui/core";
import { SubmitButton } from "../Buttons/SubmitButton";
import { PartConditionListItem } from "../ListItems/PartConditionListItem";
import Box from "@material-ui/core/Box";
import { FullScreenDialog } from "../FullScreenDialog";
import { TextAreaField } from "../Forms/fields/TextAreaField";
import { TextField } from "../Forms/fields/TextField";
import { PostJobCheckList } from "./types";
import { useAiAxiosClient } from "../../lib/aiAxiosClient";
import { useQuery } from "@apollo/client";
import { GET_JOB_FOR_INSPECTION } from "../../graphql/queries/getJob";
import { GET_INVOICE } from "../../graphql/queries/getInvoice";
import { Query, QueryGetJobArgs, QueryGetInvoiceArgs } from "../../generated/nest-graphql";
import { useAllParts } from "../../hooks/useAllParts";
import { PartsList } from "./PartsList";
import { checkListSchema } from "./validation";
import { Select } from "../Forms/fields/Select";
import { CheckboxField } from "../Forms/fields/CheckboxField";
import { ToggleButtonGroup } from "../Forms/fields/ToogleButtonGroup";
import { PART_ITEM_SELECT_OPTIONS } from "./constants";
import * as Yup from "yup";
// import { TireTreadDropdownListItem } from "../ListItems/TireTreadDropdownListItem";

const needInfoConditions = [
  "Yellow (Has wear, not in need of immediate repair)",
  "Red (Needs immediate repair)",
  "Warning lights on",
];

const startOfToday = new Date();
startOfToday.setHours(0, 0, 0, 0);
const endOfToday = new Date();
endOfToday.setHours(23, 59, 59, 59);

// const tireTreadDepthSchema = Yup.object().shape(
//   {
//     depth: Yup.string().when("unavailable", {
//       is: (unavailable) => unavailable === false || unavailable === null,
//       then: Yup.string().required(),
//       otherwise: Yup.string().nullable(),
//     }),
//     unavailable: Yup.boolean().when("depth", {
//       is: (depth) => depth === null || depth === undefined || depth?.length === 0,
//       then: Yup.boolean().oneOf([true]).required(),
//       otherwise: Yup.boolean().nullable(),
//     }),
//   },
//   [["depth", "unavailable"]]
// );

// const tireTreadSchema = Yup.object().shape({
//   frontLeft: tireTreadDepthSchema,
//   frontRight: tireTreadDepthSchema,
//   rearLeft: tireTreadDepthSchema,
//   rearRight: tireTreadDepthSchema,
// });

export const PostJobCheckListFormCore: React.FC<{
  initialValues: PostJobCheckList;
  onSubmit: (values: PostJobCheckList) => void;
  open: boolean;
  onClose: (param) => void;
  jobId?: string;
  invoiceId?: string;
}> = ({ initialValues, onSubmit, open, onClose, jobId, invoiceId }) => {
  const { data } = useQuery<Query, QueryGetJobArgs>(GET_JOB_FOR_INSPECTION, {
    variables: {
      id: jobId || "",
    },
    skip: !jobId,
  });
  const { data: invoiceData } = useQuery<Query, QueryGetInvoiceArgs>(GET_INVOICE, {
    variables: { id: invoiceId || "" },
    skip: !invoiceId,
    fetchPolicy: "no-cache",
  });
  const [isRewriting, setIsRewriting] = useState(false);
  const [rewriteError, setRewriteError] = useState<string | null>(null);
  const aiAxiosClient = useAiAxiosClient();

  const handleAIRewrite = async (currentNotes: string, setFieldValue: (field: string, value: any) => void) => {
    if (!currentNotes || currentNotes.trim().length === 0) {
      setRewriteError("Please enter some notes before rewriting");
      return;
    }

    setIsRewriting(true);
    setRewriteError(null);

    try {
      const response = await aiAxiosClient.post("/technicians/rewrite-customer-notes", {
        note: currentNotes,
      });

      if (response.data && response.data.text) {
        setFieldValue("customerNotes", response.data.text);
      } else {
        setRewriteError("No rewritten text received from AI");
      }
    } catch (error) {
      console.error("AI rewrite error:", error);
      setRewriteError("Failed to rewrite notes. Please try again.");
    } finally {
      setIsRewriting(false);
    }
  };

  const job = data?.getJob;
  const jobType = job?.type;
  const isWarrantyCheck = jobType === "Warranty Check";
  const jobStatus = job?.status;
  const isClosed = jobStatus === "Closed";

  const partItems = React.useMemo(() => {
    if (!invoiceData?.getInvoice?.services) return [];
    const invoice = invoiceData.getInvoice;
    const mappedItems: Array<{
      serviceId: string;
      serviceName: string;
      serviceItemId: string;
      name: string;
      type: string;
      warranty_replacement_reason: string | null;
      warranty_replacement_upgrade: boolean | null;
    }> = [];

    invoice.services?.forEach((service) => {
      service?.items?.forEach((item) => {
        if (item?.category === "Part" && item?.id) {
          mappedItems.push({
            serviceId: service.id,
            serviceName: service.name || "",
            serviceItemId: item.id,
            name: item.productSelection?.name || item.type || "Unknown",
            type: item.type || "",
            warranty_replacement_reason: item.warranty_replacement_reason || null,
            warranty_replacement_upgrade: item.warranty_replacement_upgrade || null,
          });
        }
      });
    });

    return mappedItems;
  }, [invoiceData?.getInvoice]);

  const partItemIds = partItems.map((item) => item.serviceItemId);

  const enhancedInitialValues = React.useMemo(() => {
    const baseValues = { ...initialValues };
    if (isWarrantyCheck && partItems.length > 0 && invoiceData?.getInvoice?.services) {
      if (!baseValues.partItems) {
        baseValues.partItems = {};
      }
      invoiceData.getInvoice.services?.forEach((service) => {
        service?.items?.forEach((item) => {
          if (item?.category === "Part" && item?.id) {
            const partItem = partItems.find((p) => p.serviceItemId === item.id);
            if (partItem) {
              baseValues.partItems[item.id] = {
                warranty_replacement_reason: item.warranty_replacement_reason || "",
                warranty_replacement_upgrade: item.warranty_replacement_upgrade || false,
              };
            }
          }
        });
      });
    }
    return baseValues;
  }, [initialValues, isWarrantyCheck, partItems, invoiceData?.getInvoice?.services]);

  const validServiceItemIds = React.useMemo(() => {
    if (!invoiceData?.getInvoice?.services) return [];
    const ids: string[] = [];
    invoiceData.getInvoice.services?.forEach((service) => {
      service?.items?.forEach((item) => {
        if (item?.category === "Part" && item?.id) {
          ids.push(item.id);
        }
      });
    });
    return ids;
  }, [invoiceData?.getInvoice?.services]);

  const allParts = useAllParts(invoiceData?.getInvoice);

  const validationSchema = React.useMemo(() => {
    const baseSchema = Yup.object().shape({
      wheelStuds: Yup.string().nullable().required("required"),
      lugnuts: Yup.string().nullable().required("required"),
      dashboardLights: Yup.string().nullable().required("required"),
      testDriveExpirience: Yup.string().nullable().required("required"),
      otherConcerns: Yup.string().nullable(),
    });

    const partItemsSchema = checkListSchema(undefined, partItemIds, validServiceItemIds, isWarrantyCheck);
    return baseSchema.concat(partItemsSchema);
  }, [partItemIds, validServiceItemIds]);

  return (
    <Formik<PostJobCheckList>
      validationSchema={validationSchema}
      initialValues={enhancedInitialValues}
      onSubmit={onSubmit}
      validateOnMount
      validateOnBlur
      enableReinitialize
    >
      {({ values, isValid, isSubmitting, errors, setFieldValue }) => {
        // console.log(errors);
        return (
          <FullScreenDialog title={"Inspection"} open={open} onClose={() => onClose(values)} headerPosition={"fixed"}>
            <Paper className="pt-20">
              <Form>
                <Box className={"px-4"}>
                  <List component="nav">
                    <PartConditionListItem name={"wheelStuds"} label={"Wheel Studs Condition"} />
                    <PartConditionListItem name={"lugnuts"} label={"Lug Nuts Torqued"} />
                    <PartConditionListItem name={"dashboardLights"} label={"Dashboard Lights"} />
                    <PartConditionListItem
                      name={"testDriveExpirience"}
                      label={"Were you able to test drive the vehicle?"}
                    />
                    <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                      <h2 className="text-lg mb-4">
                        Did you substitute or add any parts that were not on the original order?
                      </h2>
                      <PartsList parts={allParts} />
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-row">
                          <ToggleButtonGroup
                            name="usedOriginalParts"
                            items={[
                              {
                                label: "Yes",
                                value: false,
                              },
                              {
                                label: "No — only original parts were used",
                                value: true,
                              },
                            ]}
                            activeBackground="#f04e23"
                            background="#FFF2"
                          />
                        </div>
                        {values.usedOriginalParts === false && (
                          <TextField
                            name="usedOriginalPartsNotes"
                            label="Describe the different parts used"
                            rows={2}
                            multiline
                          />
                        )}
                      </div>
                    </div>
                    {isWarrantyCheck && partItems.length > 0 && (
                      <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                        <h2 className="text-lg mb-4">Warranty Attribution</h2>
                        {partItems.map((item) => {
                          const itemKey = `partItems.${item.serviceItemId}`;
                          const warrantyReasonName = `${itemKey}.warranty_replacement_reason`;
                          const warrantyUpgradeName = `${itemKey}.warranty_replacement_upgrade`;
                          return (
                            <div key={item.serviceItemId} className="mb-4 pb-4 border-b last:border-b-0">
                              <h3 className="text-md font-semibold mb-3">
                                {item.serviceName} - {item.name}
                              </h3>
                              <div className="grid grid-cols-1 gap-4">
                                <Select
                                  name={warrantyReasonName}
                                  label="Reason"
                                  options={PART_ITEM_SELECT_OPTIONS}
                                  required
                                  disabled={isClosed}
                                  fullWidth
                                />
                                <CheckboxField name={warrantyUpgradeName} label="Upgrade" disabled={isClosed} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                      <h2 className="text-lg mb-4">Any other safety/ warranty concerns?</h2>
                      <div className="flex flex-row">
                        <TextAreaField label={""} name={"otherConcerns"} placeholder="" />
                      </div>
                    </div>
                    <div className="flex flex-col p-4 border-[1px] rounded-lg mb-4">
                      <h2 className="text-lg">Customer Notes</h2>
                      <p className="text-sm text-gray-400 mb-2">
                        These notes will be visible to the customer on their invoice
                      </p>
                      <TextAreaField label={""} name="customerNotes" placeholder="" />
                      <div className="flex flex-row items-center mt-2 gap-2">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleAIRewrite(values.customerNotes || "", setFieldValue)}
                          disabled={isRewriting}
                          startIcon={isRewriting ? <CircularProgress size={16} /> : null}
                          style={{
                            borderColor: "#f04e23",
                            backgroundColor: "#f04e23",
                            color: "#fff",
                            textTransform: "none",
                            fontSize: "12px",
                          }}
                        >
                          ✨Tidy Up
                        </Button>
                        {rewriteError && <span className="text-red-500 text-xs">{rewriteError}</span>}
                      </div>
                    </div>
                    <SubmitButton isSubmitting={isSubmitting || isRewriting} isValid={isValid} label={"Submit"} />
                  </List>
                </Box>
              </Form>
            </Paper>
          </FullScreenDialog>
        );
      }}
    </Formik>
  );
};
